import { Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  ScanLine,
  FileText,
  Thermometer,
  Bell,
  Calendar,
  ClipboardList,
  AlertTriangle,
  PackageOpen,
  CheckCircle2,
  Clock,
  Shield,
  type LucideIcon,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useSelectedSite } from '@/auth/useSelectedSite';
import { useViewMode } from '@/auth/useViewMode';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SiteCard {
  siteId: string;
  siteSlug: string;
  siteName: string;
  role: 'admin' | 'supervisor' | 'security' | 'viewer' | null;
  tourCompletionPct: number | null;
  toursCompleted: number;
  toursToday: number;
  nextTourStart: string | null;
  nextTourStatus: 'scheduled' | 'in_progress' | null;
  lastScanAt: string | null;
  lastScanByName: string | null;
  openAlertsCount: number;
  hotBoxOverdue: boolean;
}

interface DashboardData {
  sites: SiteCard[];
  myRecentDar: Array<{
    id: string;
    siteSlug: string;
    siteName: string;
    occurredAt: string;
    category: string;
    description: string;
    irNumber: string | null;
  }>;
  storageAttention: Array<{
    id: string;
    siteSlug: string;
    siteName: string;
    kind: 'past_30_day_hold' | 'recent_temp_breach';
    label: string;
    detail: string;
    at: string;
  }>;
  openRoundingLogs: Array<{
    id: string;
    siteSlug: string;
    siteName: string;
    date: string;
    shift: 'day' | 'noc';
  }>;
}

export function Dashboard() {
  const { me } = useAuth();
  const { selectedSlug, selectedSite } = useSelectedSite();
  const { viewMode } = useViewMode();
  const dashQuery = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardData>('/api/dashboard'),
    refetchInterval: 60_000,
  });

  const isUserView = viewMode === 'user';
  // In user view, narrow the dashboard to just the selected site so a guard
  // sees only their site's data — even if the API returned all four. Admin
  // view keeps the broad cross-site overview.
  const rawData = dashQuery.data;
  const data: DashboardData | undefined =
    rawData && isUserView && selectedSlug
      ? {
          sites: rawData.sites.filter((s) => s.siteSlug === selectedSlug),
          myRecentDar: rawData.myRecentDar.filter((d) => d.siteSlug === selectedSlug),
          storageAttention: rawData.storageAttention.filter((s) => s.siteSlug === selectedSlug),
          openRoundingLogs: rawData.openRoundingLogs.filter((l) => l.siteSlug === selectedSlug),
        }
      : rawData;

  const firstName = me ? me.user.displayName.split(/\s+/)[0] : '';

  const headerSubtitle = (() => {
    if (data?.sites.length === 0) {
      if (isUserView && selectedSite) {
        return `No data yet for ${selectedSite.name}. Pick another site if you've moved.`;
      }
      return "You're signed in but don't have site access yet. Field tools appear here once an admin assigns you to a site.";
    }
    if (isUserView && selectedSite) {
      return `At ${selectedSite.name}. Updates every minute.`;
    }
    return `Across ${data?.sites.length ?? '…'} ${data?.sites.length === 1 ? 'site' : 'sites'}. Updates every minute.`;
  })();

  return (
    <div className="px-6 md:px-10 py-8 max-w-6xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Operations Portal
        </div>
        <h1 className="mt-1.5 text-2xl md:text-3xl font-black tracking-tight">
          {greetingPrefix()}, {firstName || 'there'}.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{headerSubtitle}</p>
      </div>

      {data && data.sites.length > 0 && (
        <>
          {!isUserView && (
            <div className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Sites
            </div>
          )}
          <div className={cn('grid gap-3 mb-8', !isUserView && 'md:grid-cols-2')}>
            {data.sites.map((s) => (
              <SiteCardView key={s.siteId} site={s} />
            ))}
          </div>

          <QuickActionsRow firstSlug={data.sites[0]!.siteSlug} />

          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <RecentActivityCard entries={data.myRecentDar} />
            <StorageAttentionCard items={data.storageAttention} />
          </div>

          {data.openRoundingLogs.length > 0 && (
            <OpenRoundingCard logs={data.openRoundingLogs} />
          )}
        </>
      )}

      {dashQuery.isLoading && (
        <div className="text-center py-10 text-sm text-muted-foreground">Loading…</div>
      )}
    </div>
  );
}

function SiteCardView({ site }: { site: SiteCard }) {
  const completionTone =
    site.tourCompletionPct == null
      ? 'text-muted-foreground'
      : site.tourCompletionPct === 100
        ? 'text-[#2C654E]'
        : site.tourCompletionPct >= 70
          ? 'text-foreground'
          : 'text-[#9F5D08]';

  return (
    <Link
      href={`/sites/${site.siteSlug}/scan`}
      className="group rounded-lg border border-card-border bg-card p-5 card-shadow hover-elevate transition-colors"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {site.role ? `Site.${cap(site.role)}` : 'Global access'}
          </div>
          <h2 className="text-lg font-black tracking-tight mt-0.5">{site.siteName}</h2>
        </div>
        {site.openAlertsCount > 0 && (
          <Link
            href={`/sites/${site.siteSlug}/alerts`}
            className="shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <Badge variant="destructive" className="gap-1">
              <Bell className="h-3 w-3" />
              {site.openAlertsCount}
            </Badge>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <Stat
          label="Today"
          value={
            site.tourCompletionPct == null
              ? '—'
              : `${site.tourCompletionPct}%`
          }
          sublabel={
            site.toursToday > 0
              ? `${site.toursCompleted}/${site.toursToday} tours`
              : 'no tours'
          }
          tone={completionTone}
        />
        <Stat
          label={
            site.nextTourStatus === 'in_progress' ? 'In progress' : 'Next tour'
          }
          value={
            site.nextTourStart
              ? formatHourMinute(site.nextTourStart)
              : '—'
          }
          sublabel={
            site.nextTourStart && site.nextTourStatus === 'scheduled'
              ? `in ${formatCountdown(site.nextTourStart)}`
              : site.nextTourStatus === 'in_progress'
                ? 'right now'
                : ''
          }
        />
        <Stat
          label="Last scan"
          value={site.lastScanAt ? formatRelative(site.lastScanAt) : '—'}
          sublabel={site.lastScanByName ?? ''}
        />
      </div>

      {site.hotBoxOverdue && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#9F5D08]">
          <Thermometer className="h-3.5 w-3.5" />
          Hot box reading overdue
        </div>
      )}

      <div className="mt-3 inline-flex items-center gap-1 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
        Open <ArrowRight className="h-3.5 w-3.5" />
      </div>
    </Link>
  );
}

function Stat({
  label,
  value,
  sublabel,
  tone,
}: {
  label: string;
  value: string;
  sublabel?: string;
  tone?: string;
}) {
  return (
    <div className="rounded-md bg-muted/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn('text-base font-mono font-semibold mt-0.5', tone ?? 'text-foreground')}>
        {value}
      </div>
      {sublabel && (
        <div className="text-[10px] text-muted-foreground truncate" title={sublabel}>
          {sublabel}
        </div>
      )}
    </div>
  );
}

function QuickActionsRow({ firstSlug }: { firstSlug: string }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <QuickAction
        href={`/sites/${firstSlug}/scan`}
        icon={ScanLine}
        label="Scan"
        tone="bg-primary text-primary-foreground"
      />
      <QuickAction
        href={`/sites/${firstSlug}/dar`}
        icon={FileText}
        label="Add DAR"
      />
      <QuickAction
        href={`/sites/${firstSlug}/storage/temp`}
        icon={Thermometer}
        label="Log Temp"
      />
      <QuickAction
        href={`/sites/${firstSlug}/rounding`}
        icon={ClipboardList}
        label="Rounding"
      />
    </div>
  );
}

function QuickAction({
  href,
  icon: Icon,
  label,
  tone,
}: {
  href: string;
  icon: LucideIcon;
  label: string;
  tone?: string;
}) {
  return (
    <Link
      href={href}
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium border transition-colors hover-elevate',
        tone ?? 'bg-background text-foreground border-border',
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </Link>
  );
}

function RecentActivityCard({ entries }: { entries: DashboardData['myRecentDar'] }) {
  return (
    <div className="rounded-lg border border-card-border bg-card p-5 card-shadow">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          My Recent Activity
        </h2>
        {entries.length > 0 && (
          <Shield className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </div>
      {entries.length === 0 ? (
        <div className="text-sm text-muted-foreground">No DAR entries yet.</div>
      ) : (
        <ul className="space-y-2">
          {entries.map((e) => (
            <li key={e.id}>
              <Link
                href={`/sites/${e.siteSlug}/dar`}
                className="block px-3 py-2 -mx-3 rounded-md hover-elevate"
              >
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <Badge variant="outline" className="capitalize text-[10px]">
                    {e.category}
                  </Badge>
                  <span className="text-muted-foreground">{e.siteName}</span>
                  <span className="text-muted-foreground ml-auto">
                    {formatRelative(e.occurredAt)}
                  </span>
                </div>
                <p className="text-sm mt-1 line-clamp-2">{e.description}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StorageAttentionCard({ items }: { items: DashboardData['storageAttention'] }) {
  return (
    <div className="rounded-lg border border-card-border bg-card p-5 card-shadow">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Needs attention
        </h2>
        {items.length > 0 && (
          <AlertTriangle className="h-3.5 w-3.5 text-[#9F5D08]" />
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4 text-[#2C654E]" />
          All clear.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const Icon =
              it.kind === 'past_30_day_hold' ? PackageOpen : Thermometer;
            const tone =
              it.kind === 'recent_temp_breach'
                ? 'text-destructive'
                : 'text-[#9F5D08]';
            const href =
              it.kind === 'past_30_day_hold'
                ? `/sites/${it.siteSlug}/storage`
                : `/sites/${it.siteSlug}/storage/temp`;
            return (
              <li key={`${it.kind}-${it.id}`}>
                <Link
                  href={href}
                  className="flex items-start gap-2 px-3 py-2 -mx-3 rounded-md hover-elevate"
                >
                  <Icon className={cn('h-4 w-4 shrink-0 mt-0.5', tone)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="font-medium">{it.label}</span>
                      <span className="text-muted-foreground">{it.siteName}</span>
                      <span className="text-muted-foreground ml-auto">
                        {formatRelative(it.at)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{it.detail}</p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function OpenRoundingCard({ logs }: { logs: DashboardData['openRoundingLogs'] }) {
  return (
    <div className="mt-6 rounded-lg border border-card-border bg-card p-5 card-shadow">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Open rounding logs
        </h2>
        <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <ul className="space-y-1">
        {logs.map((l) => (
          <li key={l.id}>
            <Link
              href={`/sites/${l.siteSlug}/rounding?date=${l.date}&shift=${l.shift}`}
              className="flex items-center gap-3 px-3 py-2 -mx-3 rounded-md hover-elevate text-sm"
            >
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="font-mono">{l.date}</span>
              <Badge variant={l.shift === 'day' ? 'outline' : 'secondary'}>
                {l.shift === 'day' ? 'Day' : 'NOC'}
              </Badge>
              <span className="text-muted-foreground">{l.siteName}</span>
              <span className="text-xs text-muted-foreground ml-auto">Uncertified</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function greetingPrefix(): string {
  const h = Number(
    new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      hour12: false,
      timeZone: 'America/Phoenix',
    }).format(new Date()),
  );
  if (h < 5) return 'Quiet shift';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 22) return 'Good evening';
  return 'Quiet night';
}

function cap(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

function formatHourMinute(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Phoenix',
  }).format(new Date(iso));
}

function formatCountdown(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'now';
  const min = Math.round(ms / 60_000);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  return `${hr}h ${min % 60}m`;
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const sec = Math.round((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Phoenix',
  }).format(d);
}

void Calendar;
